/**
 * SectionErrorBoundary — a small class component that contains a render-time
 * crash to ITS subtree instead of taking down the whole SPA (white screen).
 *
 * Motivation (E05 wire-pass regression): a single admin tab whose query threw a
 * non-ApiResultError (e.g. a ZodError from a response-schema mismatch) reached an
 * `error.detail.message` access that itself threw, and with no boundary the crash
 * propagated to the root and blanked the entire shell. Wrapping each tab panel in
 * this boundary turns "one section failed" into an inline message the user can
 * recover from (switch tabs / retry) while the rest of the app keeps working.
 *
 * Intentionally framework-minimal: React error boundaries MUST be class
 * components (no hook equivalent for `componentDidCatch`).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface SectionErrorBoundaryProps {
  /** Inline label for the failing section (e.g. "Users"). */
  readonly label?: string;
  /** The guarded subtree. */
  readonly children: ReactNode;
  /**
   * Reset key — when it changes the boundary clears its error and re-renders the
   * children. SettingsView passes the active tab id so switching tabs recovers a
   * crashed section without a full reload.
   */
  readonly resetKey?: string | number;
}

interface SectionErrorBoundaryState {
  readonly error: Error | null;
}

export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(prev: SectionErrorBoundaryProps): void {
    // A changed resetKey (e.g. tab switch) clears the captured error.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for diagnostics; do NOT rethrow (that would crash
    // the shell again, defeating the boundary).

    console.error('SectionErrorBoundary caught:', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      const label = this.props.label ? ` ${this.props.label}` : '';
      return (
        <div
          data-testid="section-error"
          role="alert"
          className="rounded-lg border border-dashed border-red-200 bg-red-50 p-6 text-sm text-red-700"
        >
          <p className="font-medium">Failed to load this{label} section.</p>
          <p className="mt-1 text-xs text-red-500">
            {this.state.error.message || 'An unexpected error occurred.'}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
